import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyIcon, ToggleLeftIcon, ToggleRightIcon, Plus, Settings, Trash2 } from 'lucide-react';
import { tenantApi, ApiKeyResponse } from '../lib/tenant-api';
import { ConfirmationModal, PageLoader } from '../components/common';
import CreateApiKeyModal from '../components/CreateApiKeyModal';
import WidgetConfigurationScript from '../components/WidgetConfigurationScript';
import PreviewKeyManager from '../components/PreviewKeyManager';
import Modal from '../components/Modal';
import { useTenantAuth } from '../contexts/TenantAuthContext';

export default function ApiKeys() {
  const { tenant } = useTenantAuth();
  const [toggleModalOpen, setToggleModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalDefaultPreview, setCreateModalDefaultPreview] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch API keys for the current tenant
  const { data: apiKeys = [], isLoading, error: fetchError } = useQuery<ApiKeyResponse[], Error>({
    queryKey: ['tenantApiKeys'],
    queryFn: tenantApi.getApiKeys,
    retry: 3,
    retryDelay: 1000,
  });

  // Fetch preview key to show badge in the table
  const { data: previewKeyData } = useQuery({
    queryKey: ['tenantPreviewApiKey'],
    queryFn: async () => {
      try {
        return await tenantApi.getPreviewApiKey();
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });

  // Mutation for toggling API key status
  const toggleApiKeyStatusMutation = useMutation({
    mutationFn: (apiKeyId: string) => tenantApi.toggleApiKeyStatus(apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantApiKeys'] });
      setError('');
    },
    onError: (err: unknown) => {
      setError(String((err as Error).message || err) || 'Failed to toggle API key status.');
    },
  });

  // Mutation for deleting API key
  const deleteApiKeyMutation = useMutation({
    mutationFn: (apiKeyId: string) => tenantApi.deleteApiKey(apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantApiKeys'] });
      setError('');
    },
    onError: (err: unknown) => {
      setError(String((err as Error).message || err) || 'Failed to delete API key.');
    },
  });

  const handleToggleApiKeyStatus = (apiKey: ApiKeyResponse) => {
    setSelectedApiKey(apiKey);
    setToggleModalOpen(true);
  };

  const handleViewConfiguration = (apiKey: ApiKeyResponse) => {
    setSelectedApiKey(apiKey);
    setConfigModalOpen(true);
  };

  const handleDeleteApiKey = (apiKey: ApiKeyResponse) => {
    setSelectedApiKey(apiKey);
    setDeleteModalOpen(true);
  };

  const confirmToggleApiKeyStatus = () => {
    if (selectedApiKey) {
      toggleApiKeyStatusMutation.mutate(selectedApiKey.id);
      setToggleModalOpen(false);
      setSelectedApiKey(null);
    }
  };

  const confirmDeleteApiKey = () => {
    if (selectedApiKey) {
      deleteApiKeyMutation.mutate(selectedApiKey.id);
      setDeleteModalOpen(false);
      setSelectedApiKey(null);
    }
  };

  const closeToggleModal = () => {
    setToggleModalOpen(false);
    setSelectedApiKey(null);
  };

  const closeConfigModal = () => {
    setConfigModalOpen(false);
    setSelectedApiKey(null);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedApiKey(null);
  };

  const handleOpenCreateModal = (asPreview = false) => {
    setCreateModalDefaultPreview(asPreview);
    setCreateModalOpen(true);
  };

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['tenantApiKeys'] });
    queryClient.invalidateQueries({ queryKey: ['tenantPreviewApiKey'] });
    // If a preview key was just created, trigger the widget to load immediately
    setTimeout(() => window.dispatchEvent(new Event('widget-api-key-changed')), 0);
  };

  const maskApiKey = (keyId: string) => {
    if (!keyId) return '';
    return `sk_...${keyId.slice(-4)}`;
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            API Keys
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage API keys for your AI chat widget integration.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => handleOpenCreateModal(false)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create API Key
          </button>
        </div>
      </div>

      {(error || fetchError) && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 ">
          {
            (error ? String(error) : '') ||
            (fetchError ? (fetchError instanceof Error ? fetchError.message : String(fetchError)) : '') ||
            'An unexpected error occurred.'
          }
        </div>
      )}

      {/* Preview Key Manager */}
      <div className="mt-8">
        <PreviewKeyManager onCreatePreviewKey={() => handleOpenCreateModal(true)} />
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:">
              <table className="w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Key Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Key ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {apiKeys.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <KeyIcon className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-sm font-medium text-gray-900 mb-2">No API keys</h3>
                          <p className="text-sm text-gray-500 mb-4">
                            Get started by creating your first API key to integrate the AI chat widget.
                          </p>
                          <button
                            type="button"
                            onClick={() => setCreateModalOpen(true)}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Your First API Key
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    apiKeys.map((key) => (
                      <tr key={key.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            <KeyIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            {key.name || 'Unnamed Key'}
                            {previewKeyData?.api_key_id === key.id && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                Preview
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {maskApiKey(key.id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${key.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {key.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(key.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewConfiguration(key)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="View Configuration"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleToggleApiKeyStatus(key)}
                              disabled={toggleApiKeyStatusMutation.isLoading}
                              className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                              title={key.is_active ? "Deactivate Key" : "Activate Key"}
                            >
                              {key.is_active ? (
                                <ToggleRightIcon className="h-4 w-4" />
                              ) : (
                                <ToggleLeftIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteApiKey(key)}
                              disabled={deleteApiKeyMutation.isLoading}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Delete Key"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        isOpen={createModalOpen}
        onClose={() => { setCreateModalOpen(false); setCreateModalDefaultPreview(false); }}
        onSuccess={handleCreateSuccess}
        defaultIsPreview={createModalDefaultPreview}
      />

      {/* Widget Configuration Modal */}
      {selectedApiKey && tenant && (
        <Modal
          isOpen={configModalOpen}
          onClose={closeConfigModal}
          title={`Widget Configuration - ${selectedApiKey.name || 'Unnamed Key'}`}
          size="xl"
        >
          <WidgetConfigurationScript
            apiKey="YOUR_API_KEY_HERE"
            tenantName={tenant.name}
            isKeyVisible={false}
          />
        </Modal>
      )}

      {/* Toggle API Key Status Confirmation Modal */}
      <ConfirmationModal
        isOpen={toggleModalOpen}
        onClose={closeToggleModal}
        onConfirm={confirmToggleApiKeyStatus}
        title={`${selectedApiKey?.is_active ? 'Deactivate' : 'Activate'} API Key`}
        message={`Are you sure you want to ${selectedApiKey?.is_active ? 'deactivate' : 'activate'} the API key "${selectedApiKey?.name || maskApiKey(selectedApiKey?.id || '')}"?`}
        confirmText={selectedApiKey?.is_active ? 'Deactivate' : 'Activate'}
        type={selectedApiKey?.is_active ? 'danger' : 'success'}
        isLoading={toggleApiKeyStatusMutation.isLoading}
      />

      {/* Delete API Key Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteApiKey}
        title="Delete API Key"
        message={`Are you sure you want to delete the API key "${selectedApiKey?.name || maskApiKey(selectedApiKey?.id || '')}"? This action cannot be undone and will immediately disable the key.`}
        confirmText="Delete"
        type="danger"
        isLoading={deleteApiKeyMutation.isLoading}
      />
    </div>
  );
}
